import React from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { color } from "echarts";

const baseSx = {
  "&.MuiDataGrid-root": {
    border: "1px solid #e0e0e0",
    borderRadius: "15px !important",
    backgroundColor: "#fff",
    overflow: "hidden !important",
  },

  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#0d6efd !important",
    fontWeight: "600",
    fontSize: "14px",
    minHeight: "38px !important",
    maxHeight: "38px !important",
    color: "#fff !important",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    whiteSpace: "normal",
    lineHeight: "1.3",
    textAlign: "center",
    color: "#fff !important",
  },

  "& .MuiDataGrid-menuIcon": {
    display: "none !important",
  },

  "& .MuiDataGrid-sortIcon": {
    color: "#fff !important",
  },

  "& .MuiDataGrid-cell": {
    padding: "4px 8px",
    fontSize: "13px",
    whiteSpace: "normal !important",
    wordBreak: "break-word",
    lineHeight: "18px",
  },

  "& .MuiDataGrid-row": {
    maxHeight: "37px !important",
    minHeight: "37px !important",
    transition: "background-color .15s ease",
  },

  "& .MuiDataGrid-row:hover": {
    backgroundColor: "#f5f7ff",
  },

  "& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover": {
    backgroundColor: "#eaf0ff !important",
  },

  "& .MuiDataGrid-cell.Mui-selected": {
    backgroundColor: "transparent !important",
  },

  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
    outline: "none !important",
    boxShadow: "none !important",
  },
  "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
    outline: "none !important",
    boxShadow: "none !important",
  },

  "& .MuiDataGrid-virtualScroller": {
    overflowY: "auto !important",
  },

  "& .MuiSvgIcon-root": {
    fontSize: "18px",
  },

  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid #e0e0e0",
    backgroundColor: "#fafafa",
    padding: "4px 12px",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    height: "48px",
    minHeight: "48px",
  },
  "& .MuiTablePagination-root": { fontSize: "13px", display: "flex", alignItems: "center", gap: "8px" },
  "& .MuiTablePagination-toolbar": {
    padding: 0,
    minHeight: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    width: "100%",
  },
  "& .MuiTablePagination-selectLabel": { margin: 0, display: "flex", alignItems: "center" },
  "& .MuiTablePagination-input": { display: "flex", alignItems: "center", margin: "0 4px" },
  "& .MuiTablePagination-displayedRows": { margin: 0, display: "flex", alignItems: "center" },
  "& .MuiSelect-select": { paddingTop: "4px !important", paddingBottom: "4px !important" },
};

const AppDataGrid = ({
  rows = [],
  columns = [],
  getRowId,
  headerColor,
  maxHeight = 400,
  headerTextColor,
  showToolbar = true,
  pagination = true,
  pageSizeOptions = [10, 20, 50],
  columnHeaderHeight = 10,
  initialState,
  density = "compact",
  disableRowSelectionOnClick = true,
  loading = false,
  sx = {},
  ...rest
}) => {
  const fallbackGetRowId = React.useCallback(
    (row) => row.id ?? row._id ?? row.grade_id ?? row.uuid,
    []
  );

  const headerSx =
    headerColor
      ? {
        "& .MuiDataGrid-columnHeaders": {
          backgroundColor: headerColor,
          color: headerTextColor ?? "#fff",
          borderBottom: `1px solid ${headerColor}`,
        },
        "& .MuiDataGrid-columnHeaders .MuiSvgIcon-root": { color: headerTextColor ?? "#fff" },
        "& .MuiDataGrid-columnHeaderTitle": { color: headerTextColor ?? "#fff" },
      }
      : {};

  const wrapperRef = React.useRef(null);
  const [selectionModel, setSelectionModel] = React.useState([]);

  React.useEffect(() => {
    const handleOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setSelectionModel([]); // clear selection
      }
    };
    document.addEventListener("pointerdown", handleOutside);
    return () => document.removeEventListener("pointerdown", handleOutside);
  }, []);

  const rowHeight = 37;

  const headerHeight = 38;

  const footerHeight = 56;

  const totalHeight = rows.length * rowHeight + headerHeight + footerHeight;

  const [pageSize, setPageSize] = React.useState(pageSizeOptions?.[1] ?? 10);


  return (
    <div ref={wrapperRef} style={{

      height: totalHeight > maxHeight ? maxHeight : totalHeight,

      overflowY: totalHeight > maxHeight ? "auto" : "hidden",
      

    }}>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={getRowId || fallbackGetRowId}
        pagination={pagination}
        pageSize={pageSize}
        onPageSizeChange={(n) => setPageSize(n)}
        rowsPerPageOptions={pageSizeOptions}
        components={showToolbar ? { Toolbar: GridToolbar } : undefined}
        disableRowSelectionOnClick={false}
        selectionModel={selectionModel}
        onSelectionModelChange={(m) => setSelectionModel(Array.isArray(m) ? m : [m])}
        density={density}
        loading={loading}
        autoHeight={totalHeight <= maxHeight}
        disableExtendRowFullWidth
        sx={[baseSx, headerSx, sx]}
        {...rest}
      />

    </div>
  );
};

export default AppDataGrid;
